/*
 * © 2025 Fannie Mae. All rights reserved.
 * This software contains confidential information and trade secrets of Fannie Mae.
 * Use, disclosure, or reproduction is prohibited without the prior written consent of Fannie Mae.
 */
import React, { forwardRef, useRef, useEffect, useState } from "react";
import Edit from "@material-ui/icons/Edit";
import { CircularProgress } from "@material-ui/core";
import { isClosedStatus, REQUEST_STATUSES } from "../../constants/RequestStatuses";
import "./EditableCellRenderer.css";
 
const EditableCellRenderer = forwardRef((params, ref) => {
  const wrapper = useRef(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Listen for assignment completion events
  useEffect(() => {
    const handleAssignmentComplete = (event) => {
      // Check if this event is for the current request
      if (event.detail?.requestId === params.data?.requestId) {
        setIsUpdating(false);
        
        // Call the parent's refresh callback if provided
        if (params.colDef?.cellRendererParams?.onDataRefresh) {
          params.colDef.cellRendererParams.onDataRefresh();
        }
      }
    };

    const handleAssignmentError = (event) => {
      // Check if this event is for the current request
      if (event.detail?.requestId === params.data?.requestId) {
        setIsUpdating(false);
      }
    };

    // Listen for assignment completion events
    document.addEventListener("assignment-complete", handleAssignmentComplete);
    document.addEventListener("assignment-error", handleAssignmentError);

    return () => {
      document.removeEventListener("assignment-complete", handleAssignmentComplete);
      document.removeEventListener("assignment-error", handleAssignmentError);
    };
  }, [params.data?.requestId, params.colDef?.cellRendererParams?.onDataRefresh]);
  
  const handleEditClick = (event) => {
    event.stopPropagation();
    setIsUpdating(true);
    
    // Check if parent component provides its own modal handler
    if (params.colDef?.cellRendererParams?.openModal) {
      // Use parent's modal handler (e.g., LenderDashboard)
      params.colDef.cellRendererParams.openModal(params.data);
    } else {
      // Use global event system (e.g., LenderRequestList)
      document.dispatchEvent(new CustomEvent("grid-edit-start", { detail: params.data }));
    }
  };
  
  return (
    <div ref={wrapper} style={{ display: "flex" }}>
      <div className="grid-editable-cell">
        <span>{params.value}</span>
        {(!isClosedStatus(params.data.requestStatus) && params.data.requestStatus !== REQUEST_STATUSES.UNRESPONSIVE) ? (
          <div className="edit-button-container">
            {isUpdating ? (
              <CircularProgress 
                size={16} 
                className="grid-edit-loading"
                aria-label="Updating assignment"
              />
            ) : (
              <Edit
                className="grid-edit-button"
                onClick={handleEditClick}
                aria-label="Edit assignment"
                tabIndex={0}
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
});

EditableCellRenderer.displayName = 'EditableCellRenderer';

export default EditableCellRenderer;